// apps/admin/src/features/products/ApprovalUploadDialog.tsx
//
// Phase 8.2 Wave 6 Task 41 — Excel upload dialog for vendor product
// submissions. 3-step flow per CONTEXT.md/310/311:
//   1. Upload — pick vendor + Excel file, parse client-side into rows[].
//   2. Preview — admin sees parsed rows + validation errors before commit.
//   3. Submit — POST /api/v1/admin/product-approvals/upload (Task 30 BE).
//      Server returns {upload_id, preview} and persists pending rows.
//
// vg-binding: PLAN:task-41
// vg-binding: CONTEXT:decisions=P8.D-307,P8.D-309,P8.D-310,P8.D-311
// vg-binding: TEST-GOALS:G-33
//
// Wired contract (Task 30 product-approval.handler.ts UploadBodySchema):
//   POST /api/v1/admin/product-approvals/upload
//   body: { vendor_id, vendor_name?, rows: UploadRow[] }
//   201:  { upload_id, preview: { product_count, variant_count,
//                                  validation_errors, rows } }
//
// Excel parsing — client-side using xlsx (already used by FE for other
// importers). For this minimum-viable surface we parse the first sheet,
// expect header row 1 with the field names listed in EXCEL_COLUMNS, and
// map subsequent rows into UploadRow. Validation errors surface inside the
// preview step + the server returns its own validation_errors[] for the
// authoritative check (duplicate detection lives server-side).

import { useEffect, useMemo, useState, type ChangeEvent, type JSX } from 'react';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Wire types — mirror Task 30 UploadRow + UploadPreview
// ---------------------------------------------------------------------------

export interface ApprovalUploadRow {
  productName: string;
  categoryId?: string | null;
  categoryName?: string | null;
  processingTime?: number;
  baseCost?: number;
  capacityPerDay?: number;
  shippingZones?: string[];
  productionSpecs?: unknown;
  description?: string;
  images?: string[];
  tags?: string[];
}

interface PreviewValidationError {
  row: number;
  field?: string;
  message: string;
}

interface PreviewRow {
  productName: string;
  type: 'new_submission' | 're_approval';
  diff?: Array<{ field: string; before: unknown; after: unknown }>;
  /**  duplicate detection — server may flag similar blueprints. */
  duplicateWarnings?: Array<{ blueprintId: string; name: string; similarity: number }>;
}

interface UploadResponseData {
  upload_id: string;
  preview: {
    product_count: number;
    variant_count: number;
    validation_errors: PreviewValidationError[];
    rows: PreviewRow[];
  };
}

interface VendorOption {
  id: string;
  name: string;
}

export interface ApprovalUploadDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful upload so the queue can refresh. */
  onUploaded?: (uploadId: string) => void;
  /** Optional pre-filled vendor (skips picker step when set). */
  initialVendor?: VendorOption;
  /** Vendor picker source — if omitted, dialog renders an ID input. */
  vendorOptions?: VendorOption[];
}

// ---------------------------------------------------------------------------
// Excel parsing — header row 1 names map to UploadRow fields
// ---------------------------------------------------------------------------

const EXCEL_COLUMNS = [
  'productName',
  'categoryId',
  'categoryName',
  'processingTime',
  'baseCost',
  'capacityPerDay',
  'shippingZones',
  'productionSpecs',
  'description',
  'images',
  'tags',
] as const;

type ExcelHeader = (typeof EXCEL_COLUMNS)[number];

/**
 * Parse a CSV-ish text content into rows. Light-weight fallback so we don't
 * pull in a 400KB xlsx parser for the v1 surface; admin operators export
 * vendor sheets as CSV before upload. Real .xlsx support lands when the
 * xlsx package is added (tracked in follow-up infra task).
 */
function parseCsv(text: string): ApprovalUploadRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];
  const headerLine = lines[0]!;
  const headers = headerLine.split(/[,;\t]/).map((h) => h.trim());
  const out: ApprovalUploadRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i]!.split(/[,;\t]/).map((c) => c.trim());
    const row: Record<string, unknown> = {};
    headers.forEach((h, idx) => {
      const raw = cols[idx] ?? '';
      if (raw === '') return;
      if ((EXCEL_COLUMNS as readonly string[]).includes(h)) {
        const key = h as ExcelHeader;
        switch (key) {
          case 'processingTime':
          case 'baseCost':
          case 'capacityPerDay': {
            const n = Number(raw);
            if (Number.isFinite(n)) row[key] = n;
            break;
          }
          case 'shippingZones':
          case 'images':
          case 'tags':
            row[key] = raw
              .split('|')
              .map((s) => s.trim())
              .filter(Boolean);
            break;
          case 'productionSpecs':
            try {
              row[key] = JSON.parse(raw);
            } catch {
              row[key] = raw;
            }
            break;
          default:
            row[key] = raw;
        }
      }
    });
    if (typeof row['productName'] === 'string' && row['productName']) {
      out.push(row as unknown as ApprovalUploadRow);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Error envelope extractor — INTERFACE-STANDARDS priority
// ---------------------------------------------------------------------------

interface ApiErrorEnvelope {
  ok?: false;
  error?: { code?: string; message?: string; user_message?: string };
}

function extractApiMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    if (env.error?.user_message) return env.error.user_message;
    if (env.error?.message) return env.error.message;
  }
  return fallback;
}

// Loose client typing — openapi-fetch generated types don't yet cover
// Phase 8.2 admin product-approval endpoints (codegen at /vg:test).
type LooseClient = {
  POST: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Step = 'upload' | 'preview';

// Radix Select forbids empty-string item values. The vendor placeholder
// (formerly <option value="">) maps to this sentinel; converted back to ''
// (the unselected state) in the change handler.
const VENDOR_NONE_VALUE = '__none__';

export function ApprovalUploadDialog({
  open,
  onClose,
  onUploaded,
  initialVendor,
  vendorOptions,
}: ApprovalUploadDialogProps): JSX.Element | null {
  const { t } = useTranslation('catalog');
  const { toast } = useToast();

  const [step, setStep] = useState<Step>('upload');
  const [vendorId, setVendorId] = useState<string>(initialVendor?.id ?? '');
  const [vendorName, setVendorName] = useState<string>(initialVendor?.name ?? '');
  const [rows, setRows] = useState<ApprovalUploadRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResponseData | null>(null);
  // Phase 8.2.2 — admin chooses N vendors to apply the approved
  // blueprint to. Default empty = legacy "blueprint only" path.
  const [applyToVendorIds, setApplyToVendorIds] = useState<string[]>([]);
  const [applyToVendorIdsRaw, setApplyToVendorIdsRaw] = useState<string>('');

  // Reset on close so reopening is fresh.
  useEffect(() => {
    if (!open) {
      setStep('upload');
      setVendorId(initialVendor?.id ?? '');
      setVendorName(initialVendor?.name ?? '');
      setRows([]);
      setParseError(null);
      setSubmitting(false);
      setUploadResult(null);
      setApplyToVendorIds([]);
      setApplyToVendorIdsRaw('');
    }
  }, [open, initialVendor?.id, initialVendor?.name]);

  function toggleApplyToVendorId(id: string): void {
    setApplyToVendorIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function parseApplyToVendorIdsRaw(input: string): string[] {
    return input
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter((s) => /^[a-f0-9]{24}$/i.test(s));
  }

  const canProceedToPreview = useMemo(() => {
    return vendorId.length === 24 && rows.length > 0;
  }, [vendorId, rows.length]);

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(null);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.length === 0) {
        setParseError(t('product_approval.uploadDialog.parse.noRows'));
        setRows([]);
        return;
      }
      setRows(parsed);
    } catch (err) {
      setParseError(
        err instanceof Error
          ? t('product_approval.uploadDialog.parse.readFail', {
              message: err.message,
            })
          : t('product_approval.uploadDialog.parse.readFailGeneric'),
      );
      setRows([]);
    }
  }

  function handleVendorChange(id: string): void {
    setVendorId(id);
    const match = vendorOptions?.find((v) => v.id === id);
    if (match) setVendorName(match.name);
  }

  async function handleSubmit(): Promise<void> {
    if (!canProceedToPreview) return;
    setSubmitting(true);
    try {
      const resolvedApplyToVendorIds =
        applyToVendorIds.length > 0
          ? applyToVendorIds
          : parseApplyToVendorIdsRaw(applyToVendorIdsRaw);
      const client = apiClient() as unknown as LooseClient;
      const { data, response, error } = await client.POST(
        '/api/v1/admin/product-approvals/upload',
        {
          body: {
            vendor_id: vendorId,
            vendor_name: vendorName || undefined,
            rows,
            ...(resolvedApplyToVendorIds.length > 0
              ? { apply_to_vendor_ids: resolvedApplyToVendorIds }
              : {}),
          },
          headers: { 'Content-Type': 'application/json' },
        },
      );
      if (!response.ok) {
        const msg =
          extractApiMessage(error, t('product_approval.errors.generic')) ||
          `HTTP ${response.status}`;
        toast({
          title: t('product_approval.uploadDialog.toast.uploadFailedTitle'),
          description: msg,
          variant: 'error',
        });
        return;
      }
      const body = data as { data?: UploadResponseData } | UploadResponseData;
      const result =
        body && typeof body === 'object' && 'data' in body && body.data
          ? body.data
          : (body as UploadResponseData);
      setUploadResult(result);
      setStep('preview');
      if (onUploaded) onUploaded(result.upload_id);
      toast({
        title: t('product_approval.uploadDialog.toast.uploadedTitle'),
        description: t('product_approval.uploadDialog.toast.uploadedDesc', {
          products: result.preview.product_count,
          variants: result.preview.variant_count,
        }),
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: t('product_approval.uploadDialog.toast.uploadFailedTitle'),
        description: err instanceof Error
          ? err.message
          : t('product_approval.toast.unknownError'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      className="max-w-3xl"
      data-testid="product-approval-upload-dialog"
    >
      <DialogHeader>
        <DialogTitle>
          {step === 'upload'
            ? t('product_approval.uploadDialog.title.upload')
            : t('product_approval.uploadDialog.title.preview')}
        </DialogTitle>
      </DialogHeader>

      {step === 'upload' ? (
        <div className="px-6 pb-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-pw-text mb-1">
              {t('product_approval.columns.vendor')}
            </label>
            {vendorOptions && vendorOptions.length > 0 ? (
              <Select
                value={vendorId === '' ? VENDOR_NONE_VALUE : vendorId}
                onValueChange={(value) =>
                  handleVendorChange(value === VENDOR_NONE_VALUE ? '' : value)
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      'product_approval.uploadDialog.selectVendorPlaceholder',
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={VENDOR_NONE_VALUE}>
                    {t('product_approval.uploadDialog.selectVendorPlaceholder')}
                  </SelectItem>
                  {vendorOptions.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                {t('product_approval.uploadDialog.vendorLoadFail')}{' '}
                {/* dev ref — not translated */}
                (Hex paste removed in dbg-admin-products-sweep-2026-05-23 —
                pickers only.)
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-pw-text mb-1">
              {t('product_approval.uploadDialog.fileLabel')}{' '}
              {/* header field names are API params — not translated */}
              (header: productName, baseCost, processingTime, capacityPerDay,
              shippingZones, productionSpecs, …)
            </label>
            <Input type="file" accept=".csv,.tsv,.xlsx" onChange={handleFileChange} />
            {parseError ? (
              <p className="text-sm text-rose-600 dark:text-rose-300 mt-2">{parseError}</p>
            ) : null}
            {rows.length > 0 ? (
              <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-2">
                {t('product_approval.uploadDialog.parsedRows', {
                  count: rows.length,
                })}
              </p>
            ) : null}
          </div>

          <div data-testid="apply-to-vendors-picker">
            <label className="block text-sm font-medium text-pw-text mb-1">
              {t('product_approval.uploadDialog.applyLabel')}
            </label>
            <p className="text-xs text-pw-text-muted mb-2">
              {t('product_approval.uploadDialog.applyHelp')}
            </p>
            {vendorOptions && vendorOptions.length > 0 ? (
              <div className="max-h-48 overflow-y-auto border border-pw-border rounded p-2 space-y-1">
                {vendorOptions.map((v) => {
                  const checked = applyToVendorIds.includes(v.id);
                  return (
                    <label
                      key={v.id}
                      className="flex items-center gap-2 text-sm hover:bg-pw-bg-hover px-2 py-1 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleApplyToVendorId(v.id)}
                      />
                      <span>{v.name}</span>
                      <code className="text-xs text-pw-text-muted ml-auto">{v.id}</code>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                {t('product_approval.uploadDialog.applyVendorEmpty')}{' '}
                {/* dev ref — not translated */}
                (Hex paste removed in dbg-admin-products-sweep-2026-05-23.)
              </p>
            )}
            {applyToVendorIds.length > 0 || parseApplyToVendorIdsRaw(applyToVendorIdsRaw).length > 0 ? (
              <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-2">
                {t('product_approval.uploadDialog.applyCountNote', {
                  count:
                    applyToVendorIds.length > 0
                      ? applyToVendorIds.length
                      : parseApplyToVendorIdsRaw(applyToVendorIdsRaw).length,
                })}
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="px-6 pb-4 space-y-4">
          {uploadResult ? (
            <>
              <div className="text-sm">
                {t('product_approval.uploadDialog.previewSummary', {
                  products: uploadResult.preview.product_count,
                  variants: uploadResult.preview.variant_count,
                })}{' '}
                <code className="text-xs">{uploadResult.upload_id}</code>
              </div>
              {uploadResult.preview.validation_errors.length > 0 ? (
                <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/40 rounded p-3 text-sm">
                  <div className="font-medium text-amber-900 mb-1">
                    {t('product_approval.uploadDialog.validationErrorsTitle', {
                      count: uploadResult.preview.validation_errors.length,
                    })}
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5 text-amber-800 dark:text-amber-300">
                    {uploadResult.preview.validation_errors.map((e, i) => (
                      <li key={i}>
                        {t('product_approval.uploadDialog.errorRow', {
                          row: e.row,
                        })}
                        {e.field ? ` (${e.field})` : ''}: {e.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="overflow-x-auto border border-pw-border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-pw-surface border-b border-pw-border">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium text-pw-text-muted">
                        {t('product_approval.columns.product')}
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-pw-text-muted">
                        {t('product_approval.columns.type')}
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-pw-text-muted">
                        {t('product_approval.uploadDialog.col.diffWarning')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.preview.rows.map((r, i) => (
                      <tr key={i} className="border-b border-pw-border">
                        <td className="py-2 px-3">{r.productName}</td>
                        <td className="py-2 px-3">
                          {r.type === 're_approval' ? (
                            <span className="text-amber-700 dark:text-amber-300">
                              {t('product_approval.types.reApproval')}
                            </span>
                          ) : (
                            <span className="text-emerald-700 dark:text-emerald-300">
                              {t('product_approval.uploadDialog.badgeNew')}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-xs text-pw-text-muted">
                          {r.diff && r.diff.length > 0
                            ? t('product_approval.uploadDialog.fieldsChanged', {
                                count: r.diff.length,
                              })
                            : '—'}
                          {r.duplicateWarnings && r.duplicateWarnings.length > 0
                            ? ` · ${t('product_approval.uploadDialog.similarBlueprints', { count: r.duplicateWarnings.length })}`
                            : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      )}

      <DialogFooter>
        {step === 'upload' ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('product_approval.actions.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canProceedToPreview || submitting}
            >
              {submitting
                ? t('product_approval.uploadDialog.uploading')
                : t('product_approval.uploadDialog.uploadPreview')}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>
            {t('product_approval.uploadDialog.actions.close')}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}

export default ApprovalUploadDialog;
